// src/pages/SearchResults_New.js
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { MapPin, Plane, Building2 } from "lucide-react";
import { useFormContext } from "../context/FormContext";
import { useCurrency } from "../context/CurrencyContext";
import Form from "../components/Form";
import MobileScrollingCalendar from "../components/MobileScrollingCalendar";
import MobileDateModal from "../components/MobileDateModal";
import SearchSummary from "../components/SearchSummary";
import ReservationJourney from "../components/ReservationJourney";
import FilterMenu from "../components/FilterMenu";
import CarSlider from "../components/CarSlider";
import FilterDisplay from "../components/FilterDisplay";
import CarDetails from "../components/CarDetails";
import SessionExpiredModal from "../components/ui/SessionExpiredModal";
import { formatLocationWithType } from "../utils/locationTypeUtils";
import {
  clearLocationTypeFilters,
  savePickupLocationTypeFilter,
  saveDropoffLocationTypeFilter,
} from "../utils/locationTypeFilterUtils";
import {
  stringToDate,
  dateToLocalString,
  formatDisplayDate,
  isBefore,
} from "../utils/dateUtils";

import { decodeSearchParams } from "../utils/urlParams";
import { formatRegionalTime } from "../components/MobileForm";
import { getCarRates } from "../api";
import { Lock, ShieldCheck, CheckCircle2, Car } from "lucide-react";

import {
  normalizeOffer,
  buildVehicleResults,
  applyFilters,
  sortVehicleResults,
} from "../utils/resultsPipeline";
import { useNavigate } from "react-router-dom";
import { NoCarsFound } from "../components/NoCarsFound";
import LoadingComponent from "../components/ui/LoadingOverlay";
import SearchSummaryBar from "../components/SeachSummaryBar";
import FilterOptions from "../components/FilterOptions";
import { registerSearch, searchById } from "../api/services/carService";
import { navigateToSearchResults } from "../utils/urlParams";

/**
 * Backend caches searches in Redis for 40 minutes.
 * We'll treat "session expired" as the cache TTL.
 */
// const SEARCH_TTL_MS = 40 * 60 * 1000;
const PAGE_SIZE = 10;

// --- helpers ---
function safeIntAge(driverAge) {
  if (!driverAge) return 35;
  // if (driverAge === "30-65") return 35;
  if (driverAge === "Above 65") return 70;
  const n = parseInt(driverAge, 10);
  return Number.isFinite(n) ? n : 35;
}

function toLocalDateTime(dateStr, timeStr) {
  if (!dateStr) return "";
  const dateOnly = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
  const t = timeStr || "10:00";
  return `${dateOnly}T${t}:00`;
}

function mapUiSortToPipeline(sortBy) {
  if (sortBy === "price-low") return "lowest-price";
  return "best-value";
}

function sortExtras(vehicleResults, sortBy) {
  if (sortBy === "price-high") {
    return [...vehicleResults].sort(
      (a, b) =>
        (b.primaryOffer?.totalPrice || 0) - (a.primaryOffer?.totalPrice || 0),
    );
  }
  if (sortBy === "name") {
    return [...vehicleResults].sort((a, b) => {
      const aa = `${a.childType || ""} ${a.model || ""}`.toLowerCase();
      const bb = `${b.childType || ""} ${b.model || ""}`.toLowerCase();
      return aa.localeCompare(bb);
    });
  }
  return vehicleResults;
}

const activities = [
  "Connecting to global rental network...",
  "Checking vehicle availability...",
  "Comparing live prices...",
  "Applying exclusive partner discounts...",
  "Finalizing best available offers...",
];

<LoadingComponent />;

export default function SearchResults({ searchIdOverride = "" } = {}) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const decoded = useMemo(
    () => decodeSearchParams(searchParams.toString()),
    [searchParams],
  );
  // const searchKey = [
  //   decoded.pickupCode,
  //   decoded.dropoffCode,
  //   decoded.pickupDate,
  //   decoded.dropoffDate,
  // ].join("|");
  const expiryWatcherPausedRef = useRef(false);
  const [dateSelectionType, setDateSelectionType] = useState("pickup");
  const [activeModal, setActiveModal] = useState(null);

  useEffect(() => {
    const alreadyStored = sessionStorage.getItem("originalSearchURL");
    if (alreadyStored) return; // NEVER overwrite

    const params = new URLSearchParams(location.search);
    const hasSearchId = params.get("search_id");

    if (hasSearchId) {
      const fullUrl = `/results${location.search}`;
      sessionStorage.setItem("originalSearchURL", fullUrl);
    }
  }, [location.search]);

  const navigate = useNavigate();
  const handleModifySearch = () => {
    sessionStorage.removeItem("originalSearchURL");
    navigate("/");
  };
  // Lock searchId permanently (prevents restore → payload switch)
const searchId =  searchIdOverride || decoded.searchId || searchParams.get("search_id") || "";
 const searchCurrencyRef = useRef(
  localStorage.getItem("search_id_currency") || null,
);
const expiryIntervalRef = useRef(null);
const expiryHandledRef = useRef(false);
const residenceLockedRef = useRef(false); // NEW

  const [carData, setCarData] = useState(null);
  const [searchMeta, setSearchMeta] = useState(null);
  const { selectedCurrency, setSelectedCurrency } = useCurrency();

  const {
    // display labels
    pickup,
    dropoff,

    // ids
    pickupGDSCode,
    dropOffGDSCode,

    // dates/times
    pickupDate,
    pickupTime,
    dropoffDate,
    dropoffTime,
    sameLocation,

    // driver
    driverCountry,
    driverAge,

    // setters for URL hydration safety
    setPickup,
    setDropoff,
    setPickupDate,
    setPickupTime,
    setDropoffDate,
    setDropoffTime,
    setSameLocation,
    setDriverCountry,
    setDriverAge,
    setPickupGDSCode,
    setDropOffGDSCode,
    startSearchSession,
    isSearchSessionExpired,
    fetchLocationSuggestions,
    setPickupSuggestions,
    localPickup,
    setLocalPickup,
    pickupSuggestions,
    setPickupLocationObj,
    localSameLocation,
    setLocalDropoff,
    setDropoffLocationObj,
    localDropoff,
    setDropoffSuggestions,
    dropoffSuggestions,
    localPickupDate,
    localDropoffDate,
    setLocalPickupDate,
    setLocalDropoffDate,
    setLocalPickupTime,
    setLocalDropoffTime,
    isTestMode,
  } = useFormContext();

  // --- UI state ---
  const [showFullForm, setShowFullForm] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [sortBy, setSortBy] = useState("most-popular");

  const [vehicleResults, setVehicleResults] = useState([]);
  const [carDataLoading, setCarDataLoading] = useState(false);
  const [carDataProcessed, setCarDataProcessed] = useState(false);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [isSearchExpired, setIsSearchExpired] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Force refetch (refresh button + expiry refresh)
  const [refreshNonce, setRefreshNonce] = useState(0);
  // for filter sort , adding here
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Dedupe identical calls on re-renders
  const lastSigRef = useRef(null);
  const activeRequestRef = useRef(0);
  const prevLocationKeyRef = useRef(null);

  // Mark that the user has visited /results this browser session so Home can
  // distinguish "back from search" (keep context) from "direct load" (clear stale).
  useEffect(() => {
    sessionStorage.setItem("srch_results_visited", "1");
  }, []);

  // useEffect(() => {
  //   setSelectedFilters({});
  // }, [searchKey]);

  // --- URL -> Context sync (refresh safe) ---
  // Unconditional: always overwrite ALL context fields from the URL on every
  // mount. This ensures FormContext is fully repopulated after the
  // refresh-detection effect in FormContext.js wipes state on a hard reload,
  // so the modify-search form and the homepage form always have correct values.
  useEffect(() => {
    if (!searchParams.toString()) return;

    // New flow — URL only has search_id
    // Hydrate from sessionStorage immediately while waiting for searchMeta
    if (decoded.searchId && !decoded.pickupCode) {
      const storedPickup = sessionStorage.getItem("pickup_location_name");
      const storedDropoff = sessionStorage.getItem("dropoff_location_name");
      const storedPickupDate = sessionStorage.getItem("search_pickupDate");
      const storedDropoffDate = sessionStorage.getItem("search_dropoffDate");
      const storedPickupTime = sessionStorage.getItem("search_pickupTime");
      const storedDropoffTime = sessionStorage.getItem("search_dropoffTime");
      const storedAge = sessionStorage.getItem("search_driverAge");
      const storedCountry = sessionStorage.getItem("search_driverCountry");

      if (storedPickup) setPickup(storedPickup);
      if (storedDropoff) setDropoff(storedDropoff);
      if (storedPickupDate) setPickupDate(storedPickupDate);
      if (storedDropoffDate) setDropoffDate(storedDropoffDate);
      if (storedPickupTime) setPickupTime(storedPickupTime);
      if (storedDropoffTime) setDropoffTime(storedDropoffTime);
      if (storedAge) setDriverAge(storedAge);
      if (storedCountry) setDriverCountry(storedCountry);
      return;
    }

    // Old flow — URL has full params (legacy path)
    setPickup(decoded.pickupLocation || "");
    setDropoff(decoded.dropoffLocation || "");
    setPickupGDSCode(decoded.pickupCode || "");
    setDropOffGDSCode(decoded.dropoffCode || "");
    setPickupDate(decoded.pickupDate || "");
    setPickupTime(decoded.pickupTime || "10:00");
    setDropoffDate(decoded.dropoffDate || "");
    setDropoffTime(decoded.dropoffTime || "10:00");
    setSameLocation(decoded.sameLocation ?? true);
    setDriverCountry(decoded.driverCountry || "US");
    setDriverAge(decoded.driverAge || "35");
    clearLocationTypeFilters();
  }, [
    searchParams,
    decoded,
    setPickup,
    setDropoff,
    setPickupGDSCode,
    setDropOffGDSCode,
    setPickupDate,
    setPickupTime,
    setDropoffDate,
    setDropoffTime,
    setSameLocation,
    setDriverCountry,
    setDriverAge,
  ]);

  useEffect(() => {
    if (expiryIntervalRef.current) return;

    expiryIntervalRef.current = setInterval(() => {
      // ✅ DO NOT CHECK EXPIRY DURING LOADING OR REFRESH
      if (carDataLoading || isRefreshing) return;

      // already handled once
      if (expiryHandledRef.current) return;

      const expired = isSearchSessionExpired();

      if (expired) {
        // console.log("SESSION EXPIRED");

        expiryHandledRef.current = true;
        setIsSearchExpired(true);
      }
    }, 3000);

    return () => {
      if (expiryIntervalRef.current) {
        clearInterval(expiryIntervalRef.current);
        expiryIntervalRef.current = null;
      }
    };
  }, [isSearchSessionExpired, carDataLoading, isRefreshing]);

  // --- effective request payload (URL first, then context) ---
  const effective = useMemo(() => {
    const pId = decoded.pickupCode || pickupGDSCode || "";
    const sameLoc = decoded.sameLocation ?? sameLocation;
    const dId = sameLoc ? pId : decoded.dropoffCode || dropOffGDSCode || "";

    const pDate = decoded.pickupDate || pickupDate || "";
    const pTime = (searchParams.has("pickupTime") && decoded.pickupTime) || pickupTime || "10:00";
    const dDate = decoded.dropoffDate || dropoffDate || "";
    const dTime = (searchParams.has("dropoffTime") && decoded.dropoffTime) || dropoffTime || "10:00";

    const residence = (searchParams.has("country") && decoded.driverCountry) || driverCountry || "US";
    const age = safeIntAge((searchParams.has("age") && decoded.driverAge) || driverAge);

    const currency = selectedCurrency?.code || "USD";

    const isValid = !!(pId && dId && pDate && dDate);

    // stable signature (only request-relevant fields)
    const signatureKey = isValid
      ? JSON.stringify({
          pId,
          dId,
          pDate,
          pTime,
          dDate,
          dTime,
          residence,
          age,
          currency,
        })
      : null;

    return {
      isValid,
      pickup_location_id: pId,
      dropoff_location_id: dId,
      pickup_datetime: toLocalDateTime(pDate, pTime),
      dropoff_datetime: toLocalDateTime(dDate, dTime),
      residence,
      age,
      currency,
      signatureKey,
    };
  }, [
    decoded,
    searchParams,
    pickupGDSCode,
    dropOffGDSCode,
    pickupDate,
    pickupTime,
    dropoffDate,
    dropoffTime,
    sameLocation,
    driverCountry,
    driverAge,
    selectedCurrency?.code,
  ]);

  // --- TTL / Expiry handling (40 mins) ---
  useEffect(() => {
    const run = async () => {
      console.log("run fired", {
        searchId,
        signatureKey: effective.signatureKey,
        refreshNonce,
      });
      if (!searchId) return;

      const currentCurrency = selectedCurrency?.code || "USD";

      console.log("currency check", {
        searchCurrencyRef: searchCurrencyRef.current,
        currentCurrency,
        willChangeCurrency:
          searchCurrencyRef.current !== null &&
          searchCurrencyRef.current !== currentCurrency,
      });

      /* =====================================================
         CURRENCY CHANGE MODE
      ===================================================== */
      if (
        searchCurrencyRef.current !== null &&
        searchCurrencyRef.current !== currentCurrency
      ) {
        if (!effective.isValid) return;

        const sig = `currency-change-${currentCurrency}-${refreshNonce}`;
        if (lastSigRef.current === sig) return;
        lastSigRef.current = sig;

        setCarDataLoading(true);
        setCarDataProcessed(false);
        setVehicleResults([]);
        setSelectedFilters({});
        const requestId = ++activeRequestRef.current;

        try {
       const registerPayload = {
        pickup_location_id: effective.pickup_location_id,
        dropoff_location_id: effective.dropoff_location_id,
        pickup_datetime: effective.pickup_datetime,
        dropoff_datetime: effective.dropoff_datetime,
        age: effective.age,
        residence: effective.residence,
        currency: effective.currency,
        is_test: isTestMode,
      };

console.log("CURRENCY CHANGE — outgoing residence:", registerPayload.residence); // NEW

const registerResponse = await registerSearch(registerPayload);
          const newSearchId = registerResponse?.search_id;

          if (!newSearchId) {
            console.error(
              "registerSearch did not return a search_id on currency change",
            );
            setVehicleResults([]);
            setSelectedFilters({});
            return;
          }

          if (requestId !== activeRequestRef.current) return;

          searchCurrencyRef.current = effective.currency;
          navigateToSearchResults(navigate, newSearchId);
        } catch (e) {
          console.error("Currency change search error:", e);
          setVehicleResults([]);
          setSelectedFilters({});
        } finally {
          if (requestId === activeRequestRef.current) {
            setCarDataLoading(false);
            setCarDataProcessed(true);
            setIsRefreshing(false);
          }
        }

        return;
      }

      /* =====================================================
         RESTORE MODE
      ===================================================== */
      const sig = `restore-${searchId}-${sortBy}-${refreshNonce}`;
      if (lastSigRef.current === sig) return;
      lastSigRef.current = sig;
      const requestId = ++activeRequestRef.current;

      setCarDataLoading(true);
      setCarDataProcessed(false);

          try {
        let resp = await searchById(searchId);
        let offers = Array.isArray(resp?.results) ? resp.results : [];

        // Freshly-registered search (e.g. after an expiry refresh) may not
        // have offers computed yet — retry briefly before treating it as empty.
        let attempts = 0;
        while (offers.length === 0 && attempts < 5) {
          if (requestId !== activeRequestRef.current) return;
          await new Promise((r) => setTimeout(r, 800));
          if (requestId !== activeRequestRef.current) return;
          resp = await searchById(searchId);
          offers = Array.isArray(resp?.results) ? resp.results : [];
          attempts++;
        }

        if (requestId !== activeRequestRef.current) return;

        console.log("RESTORE — resp.meta_info.residence:", resp?.meta_info?.residence); // NEW
        setSearchMeta(resp?.meta_info || null);
        setCarData(resp);
        // Reset ALL filters when the pickup or drop-off location changed
        const sr = resp?.search_request || resp?.meta_info || {};
        const newLocationKey = `${sr.pickup_location_id || ""}|${sr.dropoff_location_id || ""}`;
        if (prevLocationKeyRef.current && prevLocationKeyRef.current !== newLocationKey) {
          setSelectedFilters({}); // clears vendor, deposit, car type, miles, etc.
        }
        prevLocationKeyRef.current = newLocationKey;

        searchCurrencyRef.current = currentCurrency;
        localStorage.setItem("search_id_currency", currentCurrency);

        startSearchSession({
          searchId: resp?.search_id || searchId,
          originalUrl: `${location.pathname}${location.search}`,
        });
        expiryHandledRef.current = false;

        const vendorsList = resp?.vendors || [];
        const currencyToUse = resp?.currency || "USD";

        const normalized = offers.map((o) =>
          normalizeOffer(o, currencyToUse, vendorsList),
        );

        const vehicles = buildVehicleResults(normalized, false);

        const pipelineSortKey = mapUiSortToPipeline(sortBy);
        let sorted = sortVehicleResults(vehicles, pipelineSortKey);
        sorted = sortExtras(sorted, sortBy);

        setVehicleResults(sorted);
      } catch (e) {
        console.error("Restore search error:", e);
        setVehicleResults([]);
        setSelectedFilters({});
      } finally {
        if (requestId === activeRequestRef.current) {
          setCarDataLoading(false);
          setCarDataProcessed(true);
          setIsRefreshing(false);
        }
      }
    };
    expiryWatcherPausedRef.current = true;
    run();
  }, [searchId, effective.signatureKey, effective.isValid, refreshNonce]);

  // backfill pickup/dropoff labels from meta (only if missing)

  // ✅ Hydrate context when loading from cached searchId
  useEffect(() => {
    if (!searchMeta) return;

    const storedPickup = localStorage.getItem("pickup_location_name");
    const storedDropoff = localStorage.getItem("dropoff_location_name");

    setPickup(
      localStorage.getItem("pickup_location_name") ||
        searchMeta.pickup_location_name ||
        "",
    );
    setDropoff(
      localStorage.getItem("dropoff_location_name") ||
        searchMeta.dropoff_location_name ||
        "",
    );

    if (searchMeta.pickup_location_id) {
      setPickupGDSCode(searchMeta.pickup_location_id);
    }

    if (searchMeta.dropoff_location_id) {
      setDropOffGDSCode(searchMeta.dropoff_location_id);
    }

    if (searchMeta.pickup_datetime) {
      const [date, time] = searchMeta.pickup_datetime.split("T");
      setPickupDate(date);
      setPickupTime(time?.slice(0, 5));
    }

    if (searchMeta.dropoff_datetime) {
      const [date, time] = searchMeta.dropoff_datetime.split("T");
      setDropoffDate(date);
      setDropoffTime(time?.slice(0, 5));
    }

    if (searchMeta.residence && !residenceLockedRef.current) {
  setDriverCountry(searchMeta.residence);
  residenceLockedRef.current = true; // lock after first hydration so later restores (e.g. currency change) can't overwrite it
}

    if (searchMeta.age) {
      // Keep the default range ("26-70" / "30-65") if it still maps to this age,
      // so the Edit Search form keeps the "default" checkbox ticked.
      setDriverAge((prev) =>
        parseInt(prev, 10) === Number(searchMeta.age) ? prev : String(searchMeta.age),
      );
    }

    const hasLocalCurrency = localStorage.getItem("selectedCurrency");
    const isOwnSearch = localStorage.getItem("is_own_search");

    // if (searchMeta.currency && !isOwnSearch) {
    //   const currencyFromMeta = {
    //     code: searchMeta.currency,
    //     name: searchMeta.currency,
    //   };
    //   setSelectedCurrency(currencyFromMeta);
    //   localStorage.setItem(
    //     "selectedCurrency",
    //     JSON.stringify(currencyFromMeta),
    //   );
    // }
    if (searchMeta.currency && !isOwnSearch) {
      const currencyFromMeta = {
        code: searchMeta.currency,
        name: searchMeta.currency,
      };
      // Update ref BEFORE setSelectedCurrency to prevent false currency change detection
      searchCurrencyRef.current = searchMeta.currency;
      setSelectedCurrency(currencyFromMeta);
      localStorage.setItem(
        "selectedCurrency",
        JSON.stringify(currencyFromMeta),
      );
    }

  }, [searchMeta]);

  // --- Filters ---
  const filteredVehicleResults = useMemo(() => {
    const filtered = applyFilters(vehicleResults, selectedFilters);

    const pipelineSortKey = mapUiSortToPipeline(sortBy);
    let sorted = sortVehicleResults(filtered, pipelineSortKey);
    sorted = sortExtras(sorted, sortBy);

    return sorted;
  }, [vehicleResults, selectedFilters, sortBy]);

  // --- Pagination ---
  const visibleVehicles = useMemo(
    () => filteredVehicleResults.slice(0, visibleCount),
    [filteredVehicleResults, visibleCount],
  );

  const remaining = Math.max(
    filteredVehicleResults.length - visibleVehicles.length,
    0,
  );

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [selectedFilters, sortBy, filteredVehicleResults.length]);

  const handleLocationChange = async (e) => {
    const value = e.target.value;
    setPickup(value);

    if (value.trim().length >= 1) {
      const suggestions = await fetchLocationSuggestions(value);
      setPickupSuggestions(suggestions || []);
    } else {
      setPickupSuggestions([]);
    }
  };

  const handleDropoffChange = async (e) => {
    const value = e.target.value;
    setLocalDropoff(value);

    if (value.trim().length >= 1) {
      const suggestions = await fetchLocationSuggestions(value);
      setDropoffSuggestions(suggestions || []);
    } else {
      setDropoffSuggestions([]);
    }
  };

  // --- handlers ---
  // const handleRefreshSearch = useCallback(() => {
  //   // close modal immediately
  //   setIsSearchExpired(false);

  //   // mark refreshing
  //   setIsRefreshing(true);

  //   // reset expiry guard
  //   expiryHandledRef.current = false;
  //   lastSigRef.current = null;

  //   // trigger reload
  //   setRefreshNonce((n) => n + 1);

  // }, []);
  const getLocalStorageItem = (key, fallback = "") => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : fallback;
    } catch {
      return localStorage.getItem(key) || fallback;
    }
  };

  const handleRefreshSearch = useCallback(async () => {
    // If the search can't be rebuilt, close the modal and send the user to start a new search
    const giveUp = () => {
      setIsRefreshing(false);
      setIsSearchExpired(false);
      expiryHandledRef.current = false;
      navigate("/");
    };

    try {
      setIsRefreshing(true);

      // 1. Reconstruct registerPayload from localStorage with fallback to effective values
      const pickupCode = getLocalStorageItem("form_pickupGDSCode") || effective?.pickup_location_id;
      const dropoffCode = getLocalStorageItem("form_dropOffGDSCode") || effective?.dropoff_location_id;
      const pickupDateStr = getLocalStorageItem("form_pickupDate") || effective?.pickup_datetime?.split("T")[0];
      const pickupTimeStr = getLocalStorageItem("form_pickupTime") || "10:00";
      const dropoffDateStr = getLocalStorageItem("form_dropoffDate") || effective?.dropoff_datetime?.split("T")[0];
      const dropoffTimeStr = getLocalStorageItem("form_dropoffTime") || "10:00";
      const driverAgeVal = getLocalStorageItem("form_driverAge") || effective?.age;
      const driverCountryVal = getLocalStorageItem("form_driverCountry") || effective?.residence;
      const isTestModeVal = getLocalStorageItem("form_isTestMode", isTestMode);

      // Not enough info to rebuild the search -> don't call the API, start a new search
      if (!pickupCode || !pickupDateStr || !dropoffDateStr) {
        console.error("Refresh: missing search details", {
          pickupCode,
          pickupDateStr,
          dropoffDateStr,
        });
        giveUp();
        return;
      }

      const registerPayload = {
        pickup_location_id: pickupCode,
        dropoff_location_id: dropoffCode || pickupCode,
        pickup_datetime: `${pickupDateStr}T${pickupTimeStr}:00`,
        dropoff_datetime: `${dropoffDateStr}T${dropoffTimeStr}:00`,
        age: parseInt(driverAgeVal) || 35,
        residence: driverCountryVal,
        currency: effective?.currency,
        is_test: isTestModeVal,
      };

      console.log("registerPayload : : ", registerPayload);

      // 2. Call registerSearch API
      const response = await registerSearch(registerPayload);
      const newSearchId = response?.search_id;

      if (!newSearchId) {
        console.error("registerSearch failed to return search_id on session refresh");
        giveUp();
        return;
      }

      // 3. Close modal, reset guards, navigate with new search_id
      setIsSearchExpired(false);
      expiryHandledRef.current = false;
      lastSigRef.current = null;

      navigateToSearchResults(navigate, newSearchId);
    } catch (error) {
      console.error("Error refreshing expired search session:", error);
      giveUp();
    }
  }, [effective, isTestMode, navigate]);

  const onSearchFromForm = useCallback(() => {
    // URL update will change decoded → effective.signatureKey
    // Force fetch even if user searched same payload again
    setSelectedFilters({});
    setRefreshNonce((n) => n + 1);
    lastSigRef.current = null;
  }, []);
  const isReturningFromCheckout = Boolean(searchId);

  const showEmptyState =
    !carDataLoading && carDataProcessed && vehicleResults.length === 0;

  useEffect(() => {
    if (!carDataLoading && carDataProcessed && vehicleResults.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [carDataLoading, carDataProcessed, vehicleResults.length]);

  // Scroll to top when no cars found
  useEffect(() => {
    if (showEmptyState) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [showEmptyState]);
  // --- render ---
  return (
    <>
      <div className="bg-gray-50 min-h-screen pb-10 overflow-visible">
        <SearchSummaryBar onModifyClick={() => setShowFullForm(true)} />
        <div
          className={`fixed inset-0 bg-black/40 z-[99] transition-opacity duration-300 ${
            showFullForm
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setShowFullForm(false)}
        />
        <div
          className={`fixed top-16 left-0 right-0 lg:left-1/2 lg:right-auto lg:-translate-x-1/2 z-[100] bg-white shadow-xl px-4 lg:px-6 py-4 rounded-b-2xl lg:rounded-2xl transition-all duration-300 ease-out w-full lg:max-w-7xl ${
            showFullForm
              ? "opacity-100 translate-y-0"
              : "opacity-0 -translate-y-4 pointer-events-none"
          }`}
        >
          <div className="max-w-screen-xl mx-auto">
            <Form
              key={showFullForm ? "open" : "closed"}
              onClose={() => setShowFullForm(false)}
              onSearch={() => {
                onSearchFromForm();
                setShowFullForm(false);
              }}
            />
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-4">
          <div className="mt-12">
            <ReservationJourney currentStep={2} />
          </div>
          {carDataLoading ? (
            <LoadingComponent onComplete={() => setCarDataLoading(false)} />
          ) : showEmptyState ? (
            <NoCarsFound onModify={handleModifySearch} />
          ) : (
            <div className="flex flex-col lg:flex-row gap-6 ">
              <aside className="w-full lg:w-1/4">
                <FilterMenu
                  carRates={vehicleResults}
                  selectedFilters={selectedFilters}
                  onFilterChange={setSelectedFilters}
                  // ADD THESE TWO PROPS:
                  isMobileOpen={mobileFiltersOpen}
                  setIsMobileOpen={setMobileFiltersOpen}
                />
              </aside>

              <main className="w-full lg:w-3/4">
                {/* <CarSlider
                  carRates={vehicleResults}
                  selectedCarTypes={selectedFilters["Car Type"] || []}
                  currencyCode={carData?.currency || effective.currency}
                  onCarSelect={({ scopedKey, parent }) => {
                    setSelectedFilters((prev) => {
                      const current = prev["Car Type"] || [];

                      // toggle off if already active — only remove THIS specific key
                      if (current.includes(scopedKey)) {
                        const next = { ...prev };
                        const filtered = current.filter((v) => v !== scopedKey); // ← only remove exact match
                        if (filtered.length === 0) delete next["Car Type"];
                        else next["Car Type"] = filtered;
                        return next;
                      }

                      // If parent is selected, expand to individual children minus this one
                      if (current.includes(parent)) {
                        const next = { ...prev };
                        const filtered = current.filter((v) => v !== parent); // remove parent
                        if (filtered.length === 0) delete next["Car Type"];
                        else next["Car Type"] = filtered;
                        return next;
                      }

                      // toggle on — add just the scoped child key
                      return { ...prev, "Car Type": [...current, scopedKey] };
                    });
                  }}
                /> */}

                {/* <FilterDisplay
                  totalCars={vehicleResults.length}
                  filteredCars={filteredVehicleResults.length}
                  selectedFilters={selectedFilters}
                  onRemoveFilter={(category, value) => {
                    setSelectedFilters((prev) => {
                      const vals = Array.isArray(prev?.[category])
                        ? prev[category]
                        : [];
                      const nextVals = vals.filter((v) => v !== value);
                      const next = { ...prev };
                      if (nextVals.length === 0) delete next[category];
                      else next[category] = nextVals;
                      return next;
                    });
                  }}
                  onClearAllFilters={() => setSelectedFilters({})}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  onMobileFilterOpen={() => setMobileFiltersOpen(true)}
                /> */}
                <FilterOptions
                  totalCars={vehicleResults.length}
                  filteredCars={filteredVehicleResults.length}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  onMobileFilterOpen={() => setMobileFiltersOpen(true)}
                  activeFilterCount={Object.values(selectedFilters).flat().length}
                  vehicleResults={vehicleResults}
                  currencyCode={carData?.currency || effective.currency || "USD"}
                />

                <CarDetails
                  vehicleResults={visibleVehicles}
                  isLoading={carDataLoading}
                  searchId={carData?.search_id}
                  carDataProcessed={carDataProcessed}
                  currencyCode={
                    carData?.currency || effective.currency || "USD"
                  }
                  pickupLocationName={searchMeta?.pickup_location_name}
                  dropoffLocationName={searchMeta?.dropoff_location_name}
                />

                {remaining > 0 && (
                  <button
                    onClick={() =>
                      setVisibleCount((prev) =>
                        Math.min(
                          prev + PAGE_SIZE,
                          filteredVehicleResults.length,
                        ),
                      )
                    }
                    className="mt-6 w-full py-4 bg-primary text-white rounded-xl font-bold hover:bg-primary-hover transition-colors"
                  >
                    Show More Cars
                  </button>
                )}
              </main>
            </div>
          )}
        </div>

        <SessionExpiredModal
          open={isSearchExpired}
          onRefresh={handleRefreshSearch}
          isRefreshing={isRefreshing}
        />
      </div>

      {/* Pickup location search modal */}
      <MobileDateModal
        isOpen={activeModal === "location"}
        onClose={() => setActiveModal(null)}
        title="Select Location"
        subTitle="Pick-up"
      >
        <div className="space-y-4">
          <div className="relative flex items-center border border-navy/10 rounded-xl p-3 bg-gray-50 focus-within:border-gold transition-all">
            <MapPin className="text-gold w-5 h-5 mr-3" />
            <input
              autoFocus
              className="w-full bg-transparent outline-none font-bold text-navy placeholder:text-navy/30"
              placeholder="Where to pick up"
              // 1. Ensure the value is tied to your state
              value={localPickup}
              // 2. Ensure typing actually updates that state
              onChange={(e) => {
                setLocalPickup(e.target.value); // Local UI state
                setPickup(e.target.value); // Global Context state
                handleLocationChange(e); // Trigger the suggestion fetch
              }}
            />
          </div>

          <ul className="space-y-1">
            {pickupSuggestions.map((loc, i) => {
              const { mainText, subText, displayText } =
                formatLocationWithType(loc);

              // DYNAMIC ICON: Plane for airport, Building for downtown
              const IconComponent = loc.is_airport ? Plane : Building2;

              return (
                <li
                  key={i}
                  className="p-4 border-b border-navy/5 flex items-center gap-4 active:bg-gold/10 transition-colors"
                  onClick={() => {
                    const id = loc.location_id || loc.gdsCode || "";
                    setPickup(displayText);
                    setPickupLocationObj(loc);
                    setPickupGDSCode(id);
                    setPickupSuggestions([]);
                    savePickupLocationTypeFilter(
                      loc.is_airport ? "In-Terminal" : "Off-Terminal",
                    );

                    if (localSameLocation) {
                      setLocalDropoff(displayText);
                      setDropoffLocationObj(loc);
                      setDropOffGDSCode(id);
                      saveDropoffLocationTypeFilter(
                        loc.is_airport ? "In-Terminal" : "Off-Terminal",
                      );
                    }
                    setActiveModal(null);
                  }}
                >
                  {/* THEMED ICON */}
                  <div className="p-2 bg-navy/5 rounded-full shrink-0">
                    <IconComponent className="w-5 h-5 text-gold" />
                  </div>

                  <div className="flex flex-col flex-1 text-left">
                    <span className="text-sm font-bold text-navy">
                      {mainText}
                    </span>
                    <span className="text-[11px] text-navy/50">{subText}</span>
                  </div>

                  {/* THEMED BADGE */}
                  {loc.is_airport && (
                    <span className="text-[9px] px-2 py-1 bg-gold/20 text-gold rounded font-black uppercase tracking-wider">
                      Airport
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </MobileDateModal>

      {/* DROP-OFF LOCATION SEARCH MODAL */}
      <MobileDateModal
        isOpen={activeModal === "dropoff"}
        onClose={() => setActiveModal(null)}
        title="Select Location"
        subTitle="Drop-off"
      >
        <div className="space-y-4">
          <div className="relative flex items-center border border-navy/10 rounded-xl p-3 bg-gray-50 focus-within:border-gold transition-all">
            <MapPin className="text-gold w-5 h-5 mr-3" />
            <input
              autoFocus
              className="w-full bg-transparent outline-none font-bold text-navy"
              placeholder={localDropoff || "Where to drop off"}
              value={localDropoff}
              onChange={handleDropoffChange}
            />
          </div>

          <ul className="space-y-1">
            {dropoffSuggestions?.map((loc, i) => {
              const { mainText, subText, displayText } =
                formatLocationWithType(loc);
              const IconComponent = loc.is_airport ? Plane : Building2;

              return (
                <li
                  key={i}
                  className="p-4 border-b border-navy/5 flex items-center gap-4 active:bg-gold/10 transition-colors"
                  onClick={() => {
                    const id = loc.location_id || loc.gdsCode || "";
                    setLocalDropoff(displayText);
                    setDropoff(displayText);
                    setDropoffLocationObj(loc);
                    setDropOffGDSCode(id);
                    setDropoffSuggestions([]);
                    saveDropoffLocationTypeFilter(
                      loc.is_airport ? "In-Terminal" : "Off-Terminal",
                    );
                    setActiveModal(null);
                  }}
                >
                  <div className="p-2 bg-navy/5 rounded-full shrink-0">
                    <IconComponent className="w-5 h-5 text-gold" />
                  </div>

                  <div className="flex flex-col flex-1 text-left">
                    <span className="text-sm font-bold text-navy">
                      {mainText}
                    </span>
                    <span className="text-[11px] text-navy/50">{subText}</span>
                  </div>

                  {loc.is_airport && (
                    <span className="text-[9px] px-2 py-1 bg-gold/20 text-gold rounded font-black uppercase tracking-wider">
                      Airport
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </MobileDateModal>

      {/* Pickup & dropoff date modal */}
      <MobileDateModal
        isOpen={activeModal === "dates"}
        onClose={() => setActiveModal(null)}
        title="Choose Date"
        subTitle={dateSelectionType === "pickup" ? "Pick-up" : "Drop-off"}
      >
        <MobileScrollingCalendar
          selectedStartDate={stringToDate(localPickupDate)}
          selectedEndDate={stringToDate(localDropoffDate)}
          onDateSelect={(date) => {
            const selectedDate = new Date(date);
            const selectedStr = dateToLocalString(selectedDate);

            if (dateSelectionType === "pickup") {
              const selectedDate = new Date(date);
              const selectedStr = dateToLocalString(selectedDate);

              // Set pickup
              setLocalPickupDate(selectedStr);
              setPickupDate(selectedStr);

              // Auto set drop-off (+2 days)
              const dropoffDate = new Date(selectedDate);
              dropoffDate.setDate(selectedDate.getDate() + 2);

              const dropoffStr = dateToLocalString(dropoffDate);

              setLocalDropoffDate(dropoffStr);
              setDropoffDate(dropoffStr);

              // Close pickup modal
              setActiveModal(null);

              // Reopen as drop-off (after animation)
              setTimeout(() => {
                setDateSelectionType("dropoff");
                setActiveModal("dates");
              }, 500); // 250–300ms is perfect

              return;
            }

            // Drop-off selection
            if (dateSelectionType === "dropoff") {
              const pickupDateObj = stringToDate(localPickupDate);

              // Prevent invalid selection
              if (isBefore(date, pickupDateObj)) return;

              const dropoffStr = dateToLocalString(date);

              setLocalDropoffDate(dropoffStr);
              setDropoffDate(dropoffStr);

              // Close modal after drop-off selection
              setActiveModal(null);
            }
          }}
          minDate={
            dateSelectionType === "dropoff"
              ? stringToDate(localPickupDate)
              : new Date()
          }
        />
      </MobileDateModal>

      {/* Pickup & dropoff time modal */}
      <MobileDateModal
        isOpen={activeModal === "time"}
        onClose={() => setActiveModal(null)}
        title="Select Time"
      >
        <div className="grid grid-cols-3 gap-2 pb-10">
          {/* Generates 48 slots (30-minute increments) to match desktop */}
          {Array.from({ length: 48 }).map((_, i) => {
            const hour = Math.floor(i / 2)
              .toString()
              .padStart(2, "0");
            const min = i % 2 === 0 ? "00" : "30";
            const t = `${hour}:${min}`;

            return (
              <button
                key={t}
                className="py-3 border border-navy/5 rounded-xl font-bold text-navy active:bg-gold active:text-white transition-all text-sm"
                onClick={() => {
                  // Check intent to update the correct global and local states
                  if (dateSelectionType === "pickup") {
                    setLocalPickupTime(t);
                    setPickupTime(t);
                  } else {
                    setLocalDropoffTime(t);
                    setDropoffTime(t);
                  }
                  setActiveModal(null);
                }}
              >
                {/* US/CA get AM/PM; Europeans/Others get 24-hour format */}
                {formatRegionalTime(t, driverCountry)}
              </button>
            );
          })}
        </div>
      </MobileDateModal>
    </>
  );
}
